/**
 * 보안 설정 「검사 모드」 카드 화면 규칙 (W6 A1, 계약 README 「모드 스위치」·mode-keys.ts, 설계 4-5).
 * 값의 원본 = inapi security_settings. GET/PUT /admin/security/settings(/:key)(/history), 바꾸기는 M! + 사유.
 * geoMode 는 기존 GeoModeCard 그대로라 여기 없다.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/securityModes.test.mjs`).
 */

const TRI = ["off", "log", "enforce"];

/** 값은 쉬운 말로만 보인다(10-31 운영자가 코드 값을 몰라 못 바꿈) */
export const VALUE_LABEL = {
  off: "끔",
  log: "기록만",
  enforce: "막음",
  on: "켬",
  observe: "기록만",
  allow: "허용",
  block: "막음",
};
export const valueLabel = (v) => VALUE_LABEL[v] || v || "-";

/** 막는 쪽 값 — 바꿀 때 붉은 경고 */
export const isBlockingValue = (v) => v === "enforce" || v === "on" || v === "block";

/** perServer 칸에 쓸 수 있는 이름 = 이용 내역 service 이름(계약 SERVICES) + 화면 앱 이름 */
export const PER_SERVER_TARGETS = [
  "inapi",
  "crm-api",
  "call-api",
  "counsel-api",
  "chatbot",
  "websocket",
  "ai-core",
  "certificate-api",
  "contracts-api",
  "payment-worker",
  "open-api",
  "mq",
  "msg-dispatcher",
  "studio",
  "admin-hospital",
  "admin-manufacturer",
  "admin-certilife",
  "admin-reseller",
];

export const LEGACY_LABEL = {
  sharedPassword: "공용 비밀번호",
  resellerProxy: "리셀러 대리 로그인",
  resellerActing: "리셀러 대행",
};
export const LEGACY_KEYS = Object.keys(LEGACY_LABEL);

/**
 * 키 8개(계약 MODE_KEYS − geoMode). name·desc = 운영자가 읽는 쉬운 이름과 한 줄 설명(카드 줄·바꾸기 창).
 * block = 막는 값으로 바꿀 때 무엇이 막히는지(설계 4-5 「무엇」), relax = 끔으로 바꿀 때 문구(없으면 RELAX_TEXT).
 * def.values = 고를 수 있는 값, def.perServer = 서버별 덮어쓰기를 받는 키(계약 PER_SERVER_KEYS), def.initial = 배포일 값(MODE_DEFAULTS)
 */
export const SECURITY_MODE_DEFS = [
  {
    key: "auditMode",
    name: "이용 내역 필수 기록",
    desc: "막음이면 내려받기 같은 중요한 일은 이용 내역을 남기지 못할 때 하지 않고, 내려받을 때 사유를 묻습니다.",
    values: TRI,
    perServer: true,
    initial: "log",
    block: ["이용 내역을 남기지 못하면 내려받기·권한 바꾸기·지원 접속 같은 중요한 일을 하지 않습니다."],
  },
  {
    key: "permMode",
    name: "메뉴 권한 검사",
    desc:
      "막음이면 권한이 없는 메뉴·기능을 막습니다. 기록만이면 막지 않고 「막혔을 요청」만 남깁니다(관리자가 권한을 고른 직원은 늘 그 권한만). 끔이면 권한을 고른 직원도 모두 풀립니다(비상 해제).",
    values: TRI,
    perServer: true,
    initial: "log",
    block: [
      "권한이 없는 사람의 메뉴·기능 요청을 서버가 막습니다.",
      "화면도 권한이 없는 메뉴·버튼을 숨기고, 계정별 발신번호 검사와 옛 경로 닫기도 함께 켜집니다.",
    ],
    relax: "권한을 고른 직원도 모두 풀립니다(비상 해제).",
  },
  {
    key: "accountActive",
    name: "지운·잠긴 계정 막기",
    desc: "막음이면 지우거나 잠근 계정이 이미 로그인해 있어도 바로 끊습니다.",
    values: TRI,
    perServer: true,
    initial: "log",
    block: ["지우거나 잠근 계정이 아직 로그인해 있으면 바로 끊습니다."],
  },
  {
    key: "supportAccess",
    name: "지원 접속(서브 계정)",
    desc: "켜면 운영자·리셀러가 동의한 회사에 지원 계정으로 들어갈 수 있습니다.",
    values: ["off", "on"],
    perServer: false,
    initial: "off",
    block: ["운영자·리셀러가 동의한 회사에 지원 계정으로 들어갈 수 있게 됩니다."],
    relax: "새 지원 접속을 막고, 열려 있는 지원 접속도 1분 안에 모두 끝납니다.",
  },
  {
    key: "operatorActing",
    name: "운영자 대행 범위",
    desc: "운영자가 고객사 일을 대신할 때 정해진 화면에서만 하게 합니다.",
    values: TRI,
    perServer: true,
    initial: "log",
    block: ["운영자가 고객사 일을 대신할 때 정해진 화면 밖의 요청을 막습니다."],
  },
  {
    key: "wsToken",
    name: "상담 실시간 연결 확인",
    desc: "막음이면 상담 화면의 실시간 연결에 맞는 로그인 정보가 없을 때 연결하지 않습니다.",
    values: TRI,
    perServer: true,
    initial: "log",
    block: ["상담 화면의 실시간 연결에 맞는 로그인 정보가 없으면 연결을 막습니다."],
  },
  {
    key: "companyScope",
    name: "콜봇 다른 회사 막기",
    desc: "막음이면 콜봇이 자기 회사가 아닌 데이터를 요청할 때 막습니다.",
    values: ["observe", "enforce"],
    perServer: true,
    initial: "observe",
    block: ["콜봇이 자기 회사가 아닌 데이터를 요청하면 막습니다."],
  },
  {
    key: "legacyAccess",
    name: "옛 접근 방식",
    desc: "공용 비밀번호 · 리셀러 대리 로그인 · 리셀러 대행을 하나씩 허용하거나 막습니다.",
    values: ["allow", "block"],
    perServer: false,
    legacy: true,
    initial: { sharedPassword: "allow", resellerProxy: "allow", resellerActing: "allow" },
    block: {
      sharedPassword: "공용 비밀번호로 로그인하거나 챗봇 삭제 확인을 넘길 수 없고, 공용 비밀번호로 들어온 로그인도 끊습니다.",
      resellerProxy: "리셀러 「대리 로그인」(새 탭)을 막습니다. 리셀러는 「서브 관리자로 접속」만 씁니다.",
      resellerActing: "리셀러가 고객사 직원 계정을 직접 고치는 옛 방식을 막습니다.",
    },
  },
];

export const defOf = (key) => SECURITY_MODE_DEFS.find((d) => d.key === key);

/**
 * 저장에 성공한 값을 이 탭이 들고 있는 introspect 사본에 바로 넣음 — 「지원 접속」 버튼처럼 modes 를 보는 화면이
 * 새로고침 없이 따라오게(10-31 intest I17: 켠 뒤에도 새로고침 전엔 버튼이 안 보임). 서버별 값이 있는 키는
 * 이 탭 값을 여기서 정할 수 없어 그대로 둠. 다른 서버 반영은 따로 약 1분.
 */
export const withSavedMode = (introspect, def, mode) =>
  // mode 가 없는 키(옛 접근 방식 = values 만)는 건드리지 않음
  introspect && introspect.modes && def && !def.perServer && typeof mode === "string" && mode
    ? { ...introspect, modes: { ...introspect.modes, [def.key]: mode } }
    : introspect;

export const RELAX_TEXT = "막히지 않게 됩니다(기록은 남음).";

export const SOURCE_LABEL = { db: "운영 페이지", default: "기본값" };

/**
 * GET /admin/security/settings 응답 → 키 8개 줄. 응답에 없는 키는 배포일 값 + source 'default'.
 * @returns [{ def, key, mode, perServer, values, source, updatedBy, updatedAt, reason }]
 */
export const normalizeSettings = (rows) => {
  const list = Array.isArray(rows) ? rows : (rows && rows.items) || [];
  const byKey = {};
  list.forEach((r) => {
    if (r && r.key) byKey[r.key] = r;
  });
  return SECURITY_MODE_DEFS.map((def) => {
    const r = byKey[def.key] || {};
    const base = { def, key: def.key, source: r.source || "default", updatedBy: r.updatedBy || null, updatedAt: r.updatedAt || null, reason: r.reason || "" };
    if (def.legacy) return { ...base, values: { ...def.initial, ...(r.values || {}) } };
    return {
      ...base,
      mode: def.values.includes(r.mode) ? r.mode : def.initial,
      perServer: def.perServer && r.perServer && typeof r.perServer === "object" ? { ...r.perServer } : {},
    };
  });
};

/** 「지금 값」 칸 문장 */
export const currentText = (row) => {
  if (!row) return "-";
  if (row.def && row.def.legacy)
    return LEGACY_KEYS.map((k) => `${LEGACY_LABEL[k]} ${valueLabel(row.values && row.values[k])}`).join(" · ");
  const servers = Object.entries(row.perServer || {});
  const tail = servers.length ? ` · 서버별: ${servers.map(([s, v]) => `${s}=${valueLabel(v)}`).join(", ")}` : "";
  return `${valueLabel(row.mode)}${tail}`;
};

/** 이력 칸 값(문자열·{mode, perServer}·{sharedPassword…}) → 문장 */
export const valueText = (v) => {
  if (v == null) return "-";
  if (typeof v === "string") return valueLabel(v);
  if (typeof v !== "object") return String(v);
  if (LEGACY_KEYS.some((k) => k in v) && !("mode" in v))
    return LEGACY_KEYS.filter((k) => k in v).map((k) => `${LEGACY_LABEL[k]} ${valueLabel(v[k])}`).join(" · ");
  return currentText({ mode: v.mode, perServer: v.perServer, def: {} });
};

/** 바꾸기 창의 처음 값(지금 값 복사) */
export const draftOf = (row) =>
  row.def.legacy
    ? { values: { ...row.values }, reason: "" }
    : {
        mode: row.mode,
        perServer: Object.entries(row.perServer || {}).map(([server, value]) => ({ server, value })),
        reason: "",
      };

const perServerObject = (list = []) => {
  const o = {};
  list.forEach((p) => {
    if (p && p.server && p.value) o[p.server] = p.value;
  });
  return o;
};

/** 막는 쪽으로 바뀌는 칸들(새 값이 막는 값이고 지금과 다름) */
const blockingChanges = (row, draft) => {
  const out = [];
  if (row.def.legacy) {
    LEGACY_KEYS.forEach((k) => {
      if (draft.values[k] === "block" && row.values[k] !== "block") out.push(k);
    });
    return out;
  }
  if (isBlockingValue(draft.mode) && draft.mode !== row.mode) out.push("mode");
  const before = row.perServer || {};
  // 서버별 값은 그 서버가 지금 실제로 쓰는 값(perServer[서버] ?? mode)과 비교
  Object.entries(perServerObject(draft.perServer)).forEach(([s, v]) => {
    if (isBlockingValue(v) && (before[s] ?? row.mode) !== v) out.push(s);
  });
  return out;
};

/**
 * 확인창 경고. danger = 막는 쪽으로 바뀜(붉은 경고 + 무엇이 막히는지 한 줄씩), 아니면 되돌리는 쪽 문구.
 * @returns {{ danger: boolean, lines: string[] }}
 */
export const changeWarnings = (row, draft) => {
  const def = row.def;
  const changes = blockingChanges(row, draft);
  if (!changes.length) return { danger: false, lines: [def.relax && draft.mode === "off" ? def.relax : RELAX_TEXT] };
  if (def.legacy) return { danger: true, lines: changes.map((k) => `${LEGACY_LABEL[k]}: ${def.block[k]}`) };
  const where = changes.filter((c) => c !== "mode");
  const lines = [...def.block];
  if (where.length) lines.push(`서버별로 막음: ${where.join(", ")}`);
  return { danger: true, lines };
};

const sameObject = (a = {}, b = {}) => {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k]);
};

/**
 * 저장 전 검사. 괜찮으면 "", 아니면 안내 문구.
 * 기본값을 쓰는 중(source default)이면 같은 값이라도 저장할 수 있다 — 운영 페이지 값으로 고정하는 뜻(geoMode 와 같음)
 */
export const changeError = (row, draft) => {
  const def = row.def;
  if (def.legacy) {
    if (!LEGACY_KEYS.every((k) => def.values.includes(draft.values[k]))) return "값을 고르세요.";
    if (row.source === "db" && sameObject(row.values, draft.values)) return "지금과 같은 값입니다.";
  } else {
    if (!def.values.includes(draft.mode)) return "값을 고르세요.";
    const list = draft.perServer || [];
    if (list.some((p) => !PER_SERVER_TARGETS.includes(p.server) || !def.values.includes(p.value)))
      return "서버별 값의 서버와 값을 모두 고르세요.";
    const servers = list.map((p) => p.server);
    if (new Set(servers).size !== servers.length) return "같은 서버를 두 번 넣었습니다.";
    if (row.source === "db" && row.mode === draft.mode && sameObject(row.perServer, perServerObject(list)))
      return "지금과 같은 값입니다.";
  }
  // 켜기 전 기술 점검표는 inapi 문서에만(10-31 — 운영자가 알 수 없는 항목이라 저장을 막지 않음)
  if (!String(draft.reason ?? "").trim()) return "사유를 입력하세요.";
  return "";
};

/** PUT /admin/security/settings/:key 본문 {mode?, perServer?, values?, reason} */
export const buildPutBody = (row, draft) => {
  const reason = String(draft.reason ?? "").trim();
  if (row.def.legacy) return { values: { ...draft.values }, reason };
  const body = { mode: draft.mode, reason };
  if (row.def.perServer) body.perServer = perServerObject(draft.perServer);
  return body;
};

const perServerText = (o) =>
  o && typeof o === "object" && Object.keys(o).length
    ? Object.entries(o)
        .map(([s, v]) => `${s}=${valueLabel(v)}`)
        .join(", ")
    : "없음";
// 칸 이름(mode·perServer·values)은 화면에 내지 않는다(10-31 intest — 운영자가 코드 이름을 읽지 못함). 모르는 칸은 「값」
export const CHANGE_FIELD_LABEL = { mode: "기본값", perServer: "서버별", values: "" };

/** 바뀐 칸 한 개(계약 AuditChange {f, b, a} | {f, changed} | {f, added, removed}) → 문장 */
const changeItemText = (c = {}) => {
  const label = CHANGE_FIELD_LABEL[c.f] || "";
  if (c.changed) return `${label || "값"} 바뀜`;
  if (Array.isArray(c.added) || Array.isArray(c.removed))
    return `${label || "값"} +${(c.added || []).length} −${(c.removed || []).length}`;
  const fmt = c.f === "perServer" ? perServerText : valueText;
  return `${label ? `${label} ` : ""}${fmt(c.b)} → ${fmt(c.a)}`;
};

/**
 * 이력 한 줄 → 칸들. GET …/:key/history = 이용 내역 행 {at, actor, changes:[{f, b, a}], meta:{reasonNote}, outcome}(W1 구현).
 * 옛 보안 이력 모양 {createdAt, actor, detail:{before, after, reason}} 도 읽는다
 */
export const historyRow = (row = {}) => {
  const d = row.detail || {};
  const list = Array.isArray(row.changes) ? row.changes : [];
  const actor = row.actor || row.updatedBy || {};
  return {
    at: row.at || row.createdAt || row.updatedAt || null,
    who: actor.name || actor.username || actor.accountId || actor.id || "-",
    change: list.length ? list.map(changeItemText).join(" · ") : `${valueText(d.before)} → ${valueText(d.after)}`,
    reason: (row.meta && (row.meta.reasonNote || row.meta.reason)) || d.reason || row.reason || "-",
  };
};
